import { Tooltip } from "@brexhq/component-library";
import { PaletteColor } from "@brexhq/metal-foundation-shared";
import styled from "@emotion/styled";
import { padding } from "polished";
import React from "react";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { BodyM, BodyS } from "@brexhq/shared/components/Typography/Components";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import {
  ComparisonTableRowHeader,
  ComparisonTableCell as ComparisonTableCellType,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/ComparisonTable";

import { Cell, CellContent, ComparisonTableCell } from "./ComparisonTableCell";

export const Row = styled.tr<{ backgroundColor: PaletteColor }>(
  ({ backgroundColor }) => ({
    backgroundColor: backgroundColor,
    width: "100%",

    ["th, td"]: {
      backgroundColor: backgroundColor,
    },
  }),
);

const DesktopRow = styled(Row)(({ theme }) => ({
  [theme.breakpoints.SML]: {
    display: "none",
  },
}));

const MobileRow = styled(Row)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.SML]: {
    display: "table-row",
  },
}));

const MobileHeaderCell = styled.td({
  padding: 0,
  margin: 0,
});

const MobileHeaderCellContentContainer = styled.div(({ theme }) => ({
  ...padding(theme.spacing[16], theme.spacing[24], 0),
}));

const TooltipWrappedText = styled.span<{ underlineColor: PaletteColor }>(
  ({ underlineColor }) => ({
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationColor: underlineColor,
  }),
);

interface Props {
  rowHeader: ComparisonTableRowHeader;
  rowData: ComparisonTableCellType[];
  theme: ModuleThemeType;
  index: number;
  columnCount?: number;
}

export const ComparisonTableRow = ({
  rowHeader: { rowHeaderTitle, rowHeaderDescription, rowHeaderTooltipContent },
  rowData,
  theme,
  index,
  columnCount,
}: Props) => {
  const {
    standard: { textPrimary, textSecondary, background, icons },
    card: { background: cardBackground },
  } = getModuleThemeColors(theme);
  const backgroundColor = index % 2 === 0 ? cardBackground : background;

  const headerTitle = (
    <BodyM as="p" color={textPrimary}>
      {rowHeaderTitle}
    </BodyM>
  );

  const headerTitleWithTooltip = rowHeaderTooltipContent ? (
    <Tooltip text={rowHeaderTooltipContent} placement="bottom-start">
      <TooltipWrappedText underlineColor={icons}>
        {headerTitle}
      </TooltipWrappedText>
    </Tooltip>
  ) : (
    headerTitle
  );

  const headerContent = (
    <>
      {headerTitleWithTooltip}
      {rowHeaderDescription && (
        <BodyS as="p" color={textSecondary}>
          {rowHeaderDescription}
        </BodyS>
      )}
    </>
  );

  return (
    <>
      <DesktopRow backgroundColor={backgroundColor}>
        <Cell isRowHeaderCell columnCount={columnCount}>
          <CellContent isRowHeaderCell>{headerContent}</CellContent>
        </Cell>
        {rowData.map((cell) => (
          <ComparisonTableCell key={cell._key} cell={cell} theme={theme} />
        ))}
      </DesktopRow>
      <MobileRow backgroundColor={backgroundColor}>
        <MobileHeaderCell colSpan={rowData.length}>
          <MobileHeaderCellContentContainer>
            {headerContent}
          </MobileHeaderCellContentContainer>
        </MobileHeaderCell>
      </MobileRow>
      <MobileRow backgroundColor={backgroundColor}>
        {rowData.map((cell) => (
          <ComparisonTableCell key={cell._key} cell={cell} theme={theme} />
        ))}
      </MobileRow>
    </>
  );
};
